import { Row } from "@tanstack/react-table";
import { EOrderStatus } from "constants/enums";
import Decimal from "decimal.js";

import { OrderTableRow } from "./utils";

/**
 * Status priority map - lower number = higher priority
 * Groups statuses by severity/importance
 */
const STATUS_PRIORITY: Record<string, number> = {
    // Critical/Action Required (0-9)
    [EOrderStatus.payment_failed]: 0,
    [EOrderStatus.charge_failed]: 1,
    [EOrderStatus.payment_refund_failed]: 2,
    [EOrderStatus.payment_dispute_lost]: 3,
    
    // Pending/In Progress (10-19)
    [EOrderStatus.payment_pending]: 10,
    [EOrderStatus.charge_pending]: 11,
    [EOrderStatus.payment_refund_pending]: 12,
    [EOrderStatus.payment_refund_requires_action]: 13,
    [EOrderStatus.payment_dispute_open]: 14,
    [EOrderStatus.payment_dispute_evidence_submitted]: 15,
    [EOrderStatus.payment_dispute_inquiry_open]: 16,
    [EOrderStatus.payment_dispute_inquiry_evidence_submitted]: 17,
    
    // Success (20-29)
    [EOrderStatus.payment_succeed]: 20,
    [EOrderStatus.charge_succeed]: 21,
    [EOrderStatus.payment_dispute_won]: 22,
    [EOrderStatus.payment_dispute_inquiry_won]: 23,
    
    // Neutral/Closed (30-39)
    [EOrderStatus.created]: 30,
    [EOrderStatus.payment_canceled]: 31,
    [EOrderStatus.payment_refund_canceled]: 32,
    [EOrderStatus.payment_refunded]: 33,
};

/**
 * Elegant sorting functions for different data types
 */

/**
 * Sort by name (case-insensitive, handles null/undefined)
 */
export const sortByName = (
    rowA: Row<OrderTableRow>,
    rowB: Row<OrderTableRow>
): number => {
    const nameA = rowA.original.name?.toLowerCase() ?? '';
    const nameB = rowB.original.name?.toLowerCase() ?? '';
    
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Sort by price (handles Decimal type, null/undefined)
 */
export const sortByPrice = (
    rowA: Row<OrderTableRow>,
    rowB: Row<OrderTableRow>
): number => {
    const priceA = rowA.original.price;
    const priceB = rowB.original.price;
    
    // Handle null/undefined
    if (!priceA && !priceB) return 0;
    if (!priceA) return 1;
    if (!priceB) return -1;
    
    // Compare Decimal values
    const decimalA = priceA instanceof Decimal ? priceA : new Decimal(priceA);
    const decimalB = priceB instanceof Decimal ? priceB : new Decimal(priceB);
    
    return decimalA.comparedTo(decimalB);
};

/**
 * Sort by date (ISO string format, handles null/undefined)
 */
export const sortByDate = (
    rowA: Row<OrderTableRow>,
    rowB: Row<OrderTableRow>
): number => {
    const dateA = rowA.original.date;
    const dateB = rowB.original.date;
    
    // Handle null/undefined
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    // Compare as timestamps
    return new Date(dateA).getTime() - new Date(dateB).getTime();
};

/**
 * Sort by status (using priority map, handles null/undefined)
 */
export const sortByStatus = (
    rowA: Row<OrderTableRow>,
    rowB: Row<OrderTableRow>
): number => {
    const statusA = rowA.original.status;
    const statusB = rowB.original.status;
    
    // Handle null/undefined - move to end
    if (!statusA && !statusB) return 0;
    if (!statusA) return 1;
    if (!statusB) return -1;
    
    // Get priority (default to 999 for unknown statuses)
    const priorityA = STATUS_PRIORITY[statusA] ?? 999;
    const priorityB = STATUS_PRIORITY[statusB] ?? 999;
    
    const priorityDiff = priorityA - priorityB;
    
    // If same priority, sort alphabetically
    if (priorityDiff === 0) {
        return statusA.localeCompare(statusB);
    }
    
    return priorityDiff;
};

